import { $isLinkNode } from '@lexical/link';
import { $isMarkNode } from '@lexical/mark';
import { $isTextNode, LexicalNode } from 'lexical';
import { isEqual } from 'lodash';
import * as Models from 'models';
import { SegmentType } from 'services/ArtboardConverter/constants';
import { Schemas } from 'services/ArtboardConverter/models';
import { $isCustomHeadingNode } from '../nodes/CustomHeadingNode';
import { $isCustomParagraphNode } from '../nodes/CustomParagraphNode';
import { $isNoWrapNode } from '../nodes/NoWrapNode/NoWrapNode';
import { $isReferenceCitationNode } from '../nodes/ReferenceCitationNode';
import { $isSSIAnchorNode } from '../nodes/SSIAnchorNode';
import { CreatePartContext, createPart, createSegmentForText, createSegmentForLink } from './factories';

function createTextParts(children: LexicalNode[], context: CreatePartContext): Schemas.Part[] {
  return children.map((child) => {
    if (!$isTextNode(child)) {
      throw new Error(`Wrong nested child: ${child.getType()}`);
    }

    return createPart(child, context);
  });
}

function pushPartToSegment(segment: Schemas.Segment, part: Schemas.Part): void {
  const lastPart = segment.parts.at(-1);
  if (lastPart) {
    const { text: currentText, ...currentStyles } = part;
    const { text: lastText, ...lastStyles } = lastPart;
    if (isEqual(lastStyles, currentStyles)) {
      lastPart.text = lastText.concat(currentText);

      return;
    }
  }
  segment.parts.push(part);
}

function getOrCreateTextSegment(
  segments: Schemas.Segment[],
  hasNoWrap: boolean,
): Schemas.Segment {
  let lastSegment = segments.at(-1);
  const type = hasNoWrap ? SegmentType.NO_WRAP : SegmentType.TEXT;
  if (lastSegment?.type !== type) {
    lastSegment = createSegmentForText(hasNoWrap, []);
    segments.push(lastSegment);
  }

  return lastSegment;
}

function getOrCreateLinkSegment(
  segments: Schemas.Segment[],
  url: string,
  surfaces: Models.ScreensOrderedMap,
  noWrap = false,
): Schemas.LinkSegment {
  let lastSegment: Schemas.LinkSegment = segments.at(-1);
  const needsNew =
    lastSegment?.type !== SegmentType.LINK ||
    lastSegment.href !== url ||
    // if current link segment noWrap flag doesn't match required flag, create new
    Boolean(lastSegment.noWrap) !== noWrap;
  if (needsNew) {
    lastSegment = createSegmentForLink(url, [], surfaces, noWrap);
    segments.push(lastSegment);
  }

  return lastSegment;
}

const createSegmentByType = (
  child: LexicalNode,
  partContext: CreatePartContext,
  getCurrentTextSegment: (value: boolean) => Schemas.Segment,
): void => {
  if ($isNoWrapNode(child)) {
    child.getChildren().forEach((item) => {
      createSegmentByType(item, partContext, getCurrentTextSegment);
    });

    return;
  }

  if ($isReferenceCitationNode(child) || $isTextNode(child)) {
    pushPartToSegment(getCurrentTextSegment(true), createPart(child, partContext));

    return;
  }

  if ($isMarkNode(child)) {
    child.getChildren().forEach((item) => {
      const segment = getCurrentTextSegment(true);
      createTextParts([item], partContext).forEach(part => pushPartToSegment(segment, part));
    });

    return;
  }

  throw new Error(`Not implemented for no-wrap ${child.getType()}`);
};

const EMPTY_SEGMENT: Schemas.Segment = {
  parts: [],
  type: SegmentType.TEXT,
};

type Context = {
  surfaces: Models.ScreensOrderedMap;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export function createSegments(
  children: LexicalNode[],
  context: Context,
  partContext: CreatePartContext,
  isLayoutPreview = false,
): Schemas.Segment[] {
  const { surfaces } = context;
  const segments: Schemas.Segment[] = [];
  const getCurrentTextSegment = (hasNoWrap = false): Schemas.Segment => getOrCreateTextSegment(segments, hasNoWrap);
  const getCurrentLinkSegment = (url: string, noWrap = false): Schemas.LinkSegment => getOrCreateLinkSegment(segments, url, surfaces, noWrap);

  children.forEach((child) => {
    // in rare cases when user copies text from button to text cell, we can have CustomParagraphNode instead of TextNode,
    // logic is same as for nowrap
    if ($isNoWrapNode(child) || $isCustomParagraphNode(child) || $isCustomHeadingNode(child)) {
      child.getChildren().forEach((item) => {
        if ($isLinkNode(item)) {
          const linkSegment = getCurrentLinkSegment(item.getURL(), true);
          // include all text nodes under the link (may include marks)
          createTextParts(item.getAllTextNodes(), partContext).forEach(part => pushPartToSegment(linkSegment, part));

          return;
        }
        createSegmentByType(item, partContext, () => getCurrentTextSegment(true));
      });

      return;
    }
    if ($isLinkNode(child)) {
      const hasNoWrap = child.getChildren().some(c => $isNoWrapNode(c));
      const segment = getCurrentLinkSegment(child.getURL(), hasNoWrap);
      const textNodes = child.getAllTextNodes();
      createTextParts(textNodes, partContext).forEach(part => pushPartToSegment(segment, part));

      return;
    }
    if ($isMarkNode(child)) {
      child.getChildren().forEach((item) => {
        const segment = $isLinkNode(item)
          ? getCurrentLinkSegment(
            item.getURL(),
            // detect if any of link's direct children are now NoWrap nodes
            item.getChildren().some(c => $isNoWrapNode(c)),
          )
          : getCurrentTextSegment($isNoWrapNode(item));

        const textNodes = $isLinkNode(item) ? item.getAllTextNodes() : [item];
        createTextParts(textNodes, partContext).forEach(part => pushPartToSegment(segment, part));
      });

      return;
    }
    if ($isTextNode(child) || $isReferenceCitationNode(child)) {
      pushPartToSegment(getCurrentTextSegment(false), createPart(child, partContext, isLayoutPreview));

      return;
    }

    if ($isSSIAnchorNode(child)) {
      return;
    }

    throw new Error(`Not implemented for ${child.getType()}`);
  });

  // empty segment should be added to have the same output as Draftjs
  if (!segments.length) {
    segments.push(EMPTY_SEGMENT);
  }

  return segments;
}
